import React from "react";
import { gstDay } from "../../../../shared/utils/gstApplicability";
import {
  FeatherCheckCircle,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherInfo,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Avatar } from "../../../../ui/components/Avatar";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { formatDate, truncateText } from "../../../../shared/utils/formatters";
import { Badge } from "../../../../ui/components/Badge";
import { Tooltip } from "../../../../ui/components/Tooltip";
import AgentReviewStatusBadge from "../../../agent-review/presentation/components/AgentReviewStatusBadge";
import { AgentReviewSummary, BillEntity, PaginationInfo } from "../../domain";
import { BillStatusChip } from "./BillStatusChip";

interface BillTableProps {
  bills: BillEntity[];
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  itemsPerPage: number;
  searchTerm: string;
  onReview: (bill: BillEntity) => void;
  onPageChange: (page: number) => void;
}

export const BillTable: React.FC<BillTableProps> = ({
  bills,
  loading,
  inlineLoading = false,
  error,
  pagination,
  itemsPerPage,
  searchTerm,
  onReview,
  onPageChange,
}) => {
  return (
    <div className="flex w-full flex-col items-start">
      {/* ZOUR-67 (change 7): two more columns push the table past its container
          at 1280, and the parent clips rather than scrolls — the Review button
          simply disappeared off the right. Same wrapper the Agent Review queue
          uses, for the same reason. Pagination stays outside it so the controls
          do not scroll away with the rows. */}
      <div className="w-full overflow-x-auto">
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Bills Number</Table.HeaderCell>
            <Table.HeaderCell>Vendor</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Agent Status</Table.HeaderCell>
            <Table.HeaderCell>Agent Findings</Table.HeaderCell>
            <Table.HeaderCell>Source</Table.HeaderCell>
            <Table.HeaderCell>Uploaded Date</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={itemsPerPage} columnCount={9} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={9}>
              <div className="flex w-full items-center justify-center py-8">
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={9}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : bills.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={9}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-neutral-500">
                  {searchTerm.trim()
                    ? "No bills match your search"
                    : "No bills found"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          bills.map((bill) => (
            <BillTableRow key={bill.id} bill={bill} onReview={onReview} />
          ))
        )}
      </Table>
      </div>

      {!loading && !inlineLoading && pagination && (
        <BillPagination
          pagination={pagination}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

interface BillTableRowProps {
  bill: BillEntity;
  onReview: (bill: BillEntity) => void;
}

const BillTableRow: React.FC<BillTableRowProps> = ({ bill, onReview }) => {
  const vendorName = getDocumentName(bill);

  return (
    <Table.Row>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          {/* ZOUR-67 (change 8): capped for the same reason as the vendor cell.
              A 24-character bill number was nowrap with no ceiling, so pages
              carrying one (e.g. "INV/AKR/CH/Professional and Expense/2025-26/17")
              pushed the table 41px past its wrapper while shorter pages fitted.
              The "Issued on …" caption below sets the real floor for this
              column, so capping here costs nothing on a normal row. */}
          <span
            className="max-w-[150px] truncate whitespace-nowrap text-body-bold font-body-bold text-default-font"
            title={getDocumentNumber(bill)}
          >
            {truncateText(getDocumentNumber(bill), 24)}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Issued on {getIssueDateLabel(bill)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center gap-2">
          <Avatar size="small" square={true}>
            {vendorName.charAt(0).toUpperCase()}
          </Avatar>
          {/* ZOUR-67 (change 8): 240px here is what pushed the 9-column table
              past its wrapper at 1280 and put the Review button off-screen.
              Narrower, with the full name on hover, so nothing is lost —
              `title` carries `vendorName`, not the already-shortened string. */}
          <span
            className="max-w-[150px] truncate whitespace-nowrap text-body font-body text-default-font"
            title={vendorName}
          >
            {truncateText(vendorName, 35)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
          {getDocumentAmount(bill)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          <BillStatusChip bill={bill} />
          {bill.updated_at && (
            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
              {getStatusDateLabel(bill)}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        <AgentStatusCell review={bill.agent_review} />
      </Table.Cell>
      <Table.Cell>
        <AgentFindingsCell review={bill.agent_review} />
      </Table.Cell>
      <Table.Cell>
        <SourceCell
          source={getSourceText(bill)}
          fileName={bill.original_filename || "N/A"}
        />
      </Table.Cell>
      <Table.Cell>
        <span className="whitespace-nowrap text-body font-body text-subtext-color">
          {formatDateOrNA(bill.created_at)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Button variant="neutral-primary" onClick={() => onReview(bill)}>
          Review
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

/**
 * ZOUR-67 (change 7) — the background reviewer's verdict, on the page the
 * accountant already works from.
 *
 * "—" is not a failure state: it is every bill the agent will never look at
 * (posted, rejected, duplicate, or not an upload) plus the case where its
 * tables are unavailable. Showing "Checking" there would promise work that is
 * not coming, which is the same mistake the queue page's posted-bill rule
 * exists to avoid.
 */
const AgentStatusCell: React.FC<{ review?: AgentReviewSummary | null }> = ({
  review,
}) => {
  if (!review) {
    return (
      <span className="text-body font-body text-subtext-color">—</span>
    );
  }

  if (review.status === "no_vendor") {
    return (
      <SubframeCore.Tooltip.Provider>
        <SubframeCore.Tooltip.Root>
          <SubframeCore.Tooltip.Trigger asChild={true}>
            <span className="inline-flex">
              <Badge variant="neutral">No vendor</Badge>
            </span>
          </SubframeCore.Tooltip.Trigger>
          <SubframeCore.Tooltip.Portal>
            <SubframeCore.Tooltip.Content asChild={true} sideOffset={4}>
              <Tooltip>
                Pick a vendor and the background check will review this bill on
                its next run.
              </Tooltip>
            </SubframeCore.Tooltip.Content>
          </SubframeCore.Tooltip.Portal>
        </SubframeCore.Tooltip.Root>
      </SubframeCore.Tooltip.Provider>
    );
  }

  return <AgentReviewStatusBadge status={review.status} stale={review.stale} />;
};

/**
 * Counts, never a bare zero.
 *
 * "0" in a findings column reads as "nothing wrong", and that is exactly what
 * it does not mean: a bill is Needs attention with zero findings whenever the
 * rule validator blocked it or the document could not be read. Only a Ready
 * bill earns the tick.
 */
const AgentFindingsCell: React.FC<{ review?: AgentReviewSummary | null }> = ({
  review,
}) => {
  if (!review || review.status === "no_vendor" || review.status === "checking") {
    return null;
  }

  const { mismatch_count: mismatches, advisory_count: advisories } = review;

  if (review.status === "ready" && mismatches === 0 && advisories === 0) {
    return (
      <AgentFindingsTooltip text="Nothing to flag: the bill matches its document and follows this vendor's history.">
        <FeatherCheckCircle className="text-body font-body text-success-600" />
      </AgentFindingsTooltip>
    );
  }

  if (mismatches === 0 && advisories === 0) return null;

  return (
    <div className="flex items-center gap-1">
      {mismatches > 0 && (
        <AgentFindingsTooltip
          text={`${mismatches} finding${mismatches === 1 ? "" : "s"} that contradict the document. These block posting.`}
        >
          <span className="inline-flex">
            <Badge variant="error">{mismatches}</Badge>
          </span>
        </AgentFindingsTooltip>
      )}
      {advisories > 0 && (
        <AgentFindingsTooltip
          text={`${advisories} booking choice${advisories === 1 ? "" : "s"} unlike this vendor's history. These do not block posting.`}
        >
          <span className="inline-flex">
            <Badge variant="warning">{advisories}</Badge>
          </span>
        </AgentFindingsTooltip>
      )}
    </div>
  );
};

const AgentFindingsTooltip: React.FC<{
  text: string;
  children: React.ReactNode;
}> = ({ text, children }) => (
  <SubframeCore.Tooltip.Provider>
    <SubframeCore.Tooltip.Root>
      <SubframeCore.Tooltip.Trigger asChild={true}>
        <span className="inline-flex cursor-default">{children}</span>
      </SubframeCore.Tooltip.Trigger>
      <SubframeCore.Tooltip.Portal>
        <SubframeCore.Tooltip.Content asChild={true} sideOffset={4}>
          <Tooltip>{text}</Tooltip>
        </SubframeCore.Tooltip.Content>
      </SubframeCore.Tooltip.Portal>
    </SubframeCore.Tooltip.Root>
  </SubframeCore.Tooltip.Provider>
);

interface SourceCellProps {
  source: string;
  fileName: string;
}

const SourceCell: React.FC<SourceCellProps> = ({ source, fileName }) => (
  <div className="flex items-center gap-1">
    <span className="whitespace-nowrap text-body font-body text-neutral-500">
      {source}
    </span>
    <SubframeCore.Tooltip.Provider>
      <SubframeCore.Tooltip.Root>
        <SubframeCore.Tooltip.Trigger asChild={true}>
          <FeatherInfo className="cursor-pointer text-body font-body text-neutral-400" />
        </SubframeCore.Tooltip.Trigger>
        <SubframeCore.Tooltip.Portal>
          <SubframeCore.Tooltip.Content
            side="top"
            align="center"
            sideOffset={4}
            asChild={true}
          >
            <div className="flex items-center gap-2 rounded-md bg-neutral-900 px-3 py-2 shadow-lg">
              <span className="max-w-[280px] truncate text-caption font-caption text-white">
                {fileName}
              </span>
            </div>
          </SubframeCore.Tooltip.Content>
        </SubframeCore.Tooltip.Portal>
      </SubframeCore.Tooltip.Root>
    </SubframeCore.Tooltip.Provider>
  </div>
);

interface BillPaginationProps {
  pagination: PaginationInfo;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const BillPagination: React.FC<BillPaginationProps> = ({
  pagination,
  itemsPerPage,
  onPageChange,
}) => {
  const currentPage = pagination.current_page || 1;
  const totalPages = pagination.total_pages || 0;

  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between mt-4 flex-shrink-0">
      <span className="text-body font-body text-subtext-color">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, pagination.total)} of{" "}
        {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {getVisiblePages(currentPage, totalPages).map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={
                currentPage === pageNumber
                  ? "brand-primary"
                  : "neutral-tertiary"
              }
              size="small"
              onClick={() => onPageChange(pageNumber)}
              className="min-w-[32px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.has_more}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
    if (totalPages <= 5) return index + 1;
    if (currentPage <= 3) return index + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + index;
    return currentPage - 2 + index;
  });
};

type BillWithResponseDates = BillEntity & {
  date?: string | null;
  bill_date?: string | null;
  invoice_date?: string | null;
  issue_date?: string | null;
};

const getDocumentAmount = (bill: BillEntity): string => {
  const total = bill.bill_json?.total;
  if (total !== undefined && total !== null) {
    const numericTotal = typeof total === "number" ? total : Number(total);
    const displayTotal = Number.isFinite(numericTotal)
      ? numericTotal.toLocaleString("en-IN")
      : String(total);

    return `${bill.bill_json.currency_symbol || ""}${displayTotal}`;
  }

  return "N/A";
};

const getDocumentName = (bill: BillEntity): string => {
  return bill.bill_json?.vendor_name || "Unknown Vendor";
};

const getDocumentNumber = (bill: BillEntity): string => {
  return bill.bill_json?.bill_number || bill.id.toString();
};

const getSourceText = (bill: BillEntity): string => {
  const source = bill.source || "upload";
  return source.charAt(0).toUpperCase() + source.slice(1);
};

const getIssueDateLabel = (bill: BillEntity): string => {
  const responseBill = bill as BillWithResponseDates;
  // Prefer document `date` over legacy `invoice_date` / `bill_date` (often stale).
  const date =
    getNonEmptyString(bill.bill_json?.date) ||
    getNonEmptyString(responseBill.date) ||
    getNonEmptyString(bill.bill_json?.invoice_date) ||
    getNonEmptyString(responseBill.bill_date) ||
    getNonEmptyString(responseBill.invoice_date) ||
    getNonEmptyString(responseBill.issue_date);

  return formatDateOrNA(date);
};

const getNonEmptyString = (value: unknown): string | null => {
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

const formatDateOrNA = (dateString?: string | null): string => {
  if (!dateString) return "N/A";

  // ZOUR-19 (review 15): normalise first. Documents captured before the
  // transfer started writing the `date` column fall back to the JSON date,
  // which is the extractor's DD/MM/YYYY — `new Date("15/05/2026")` is Invalid
  // Date, so this cell read "N/A" on a document that plainly has a date on it.
  // gstDay() is the codebase's day-first parser; using it here keeps one
  // reading of a date rather than a second, subtly different one.
  const normalised = gstDay(dateString) ?? dateString;

  const date = new Date(normalised);
  if (Number.isNaN(date.getTime())) return "N/A";

  return formatDate(normalised, "medium");
};

const getStatusDateLabel = (bill: BillEntity): string => {
  if (bill.status === "4") {
    return `Rejected at ${formatDate(bill.updated_at, "medium")}`;
  }

  if (bill.status === "3") {
    return `Duplicated at ${formatDate(bill.updated_at, "medium")}`;
  }

  return `Modified ${formatDate(bill.updated_at, "medium")}`;
};
